import { Helmet } from 'react-helmet-async';
import { Box, Container, Grid, Typography, Tab, useTheme, Breadcrumbs, Link } from '@mui/material';
import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import ChevronRightIcon from '../../icons/ChevronRight';
import DifferenceIcon from '@mui/icons-material/Difference';
import ShiftStatistiqueFinished from '../../components/dashboard/shift/ShiftStatistiqueFinished';
import useAuth from '../../hooks/useAuth';
import ShiftStatistiqueNotFinished from '../../components/dashboard/shift/ShiftStatistiqueNotFinished';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import ShiftedLearnersTable from './ShiftedLearnersTable';
import CustomersFollowUpTable from '../../components/dashboard/customer/CustomersFollowUpTable';
import { alpha } from '@mui/material/styles';
import { lightVeil, darkVeil, greyAlpha, grey } from '../../theme/tones';
import usePermissions from '../../hooks/usePermissions';

const Shift = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { can } = usePermissions();

  const [tabs, setTabs] = React.useState('apprenants');

  const handleChange = (event, tab) => {
    setTabs(tab);
  };

  return (
    <>
      <Helmet>
        <title>Suivi des apprenants | Syndays</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: theme.palette.mode === 'light' ? grey(50) : grey(950),
          minHeight: '100%',
          py: 4,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Soft glowing background blur spots for premium design */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            left: -100,
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: (theme) => `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0)} 70%)`,
            pointerEvents: 'none',
            zIndex: 0
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 200,
            right: -100,
            width: 500,
            height: 500,
            borderRadius: '50%',
            background: (theme) => `radial-gradient(circle, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.main, 0)} 70%)`,
            pointerEvents: 'none',
            zIndex: 0
          }}
        />

        <Container maxWidth={false} disableGutters sx={{ px: 2, position: 'relative', zIndex: 1 }}>
          {/* ── Hero Header ── */}
          <Box
            sx={{
              mb: 4,
              p: 3.5,
              borderRadius: '24px',
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              backdropFilter: 'blur(20px)',
              boxShadow: theme.palette.mode === 'light'
                ? `0 4px 24px ${darkVeil(0.04)}`
                : `0 4px 24px ${darkVeil(0.2)}`,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '16px',
                  background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: (theme) => `0 8px 20px ${alpha(theme.palette.secondary.main, 0.3)}`,
                  flexShrink: 0,
                }}
              >
                <DifferenceIcon sx={{ color: grey(0), fontSize: '1.7rem' }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: '1.5rem',
                    color: 'text.primary',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                  }}
                >
                  Suivi des apprenants
                </Typography>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<ChevronRightIcon fontSize="small" />}
                  sx={{ mt: 1 }}
                >
                  <Link
                    color="textSecondary"
                    component={RouterLink}
                    to="/dashboard"
                    variant="subtitle2"
                    sx={{ textDecoration: 'none', '&:hover': { color: 'secondary.main' } }}
                  >
                    Tableau de bord
                  </Link>
                  <Typography
                    color="textSecondary"
                    variant="subtitle2"
                  >
                    Suivi des apprenants
                  </Typography>
                </Breadcrumbs>
              </Box>
            </Box>
          </Box>
            <Grid
              alignItems="center"
              container
              spacing={3}
              item
              xs={12}
            >
              {can('shift.view', user.access_level === 0 || user.id_service === 4) ? (
                <Box sx={{ width: '100%', mt: 2 }}>
                  <TabContext value={tabs}>
                    <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
                      <TabList
                        onChange={handleChange}
                        aria-label="lab API tabs example"
                        sx={{
                          backgroundColor: theme.palette.mode === 'light' ? greyAlpha(100, 0.8) : greyAlpha(800, 0.5),
                          backdropFilter: 'blur(8px)',
                          borderRadius: '16px',
                          p: 0.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          minHeight: 'auto',
                          display: 'inline-flex',
                          '& .MuiTabs-flexContainer': {
                            gap: 0.5
                          },
                          '& .MuiTabs-indicator': {
                            height: '100%',
                            borderRadius: '12px',
                            backgroundColor: theme.palette.mode === 'light' ? grey(0) : lightVeil(0.08),
                            boxShadow: theme.palette.mode === 'light' ? `0 4px 12px ${darkVeil(0.05)}` : 'none',
                            border: theme.palette.mode === 'light' ? 'none' : `1px solid ${lightVeil(0.1)}`
                          },
                          '& .MuiTab-root': {
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            textTransform: 'none',
                            minWidth: 100,
                            minHeight: 40,
                            py: 1,
                            px: 2.5,
                            borderRadius: '12px',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            zIndex: 1,
                            color: 'text.secondary',
                            '&.Mui-selected': {
                              color: 'primary.main',
                            },
                            '&:hover': {
                              color: 'primary.main',
                              opacity: 0.9,
                              transform: 'translateY(-1px)'
                            }
                          }
                        }}
                      >
                        <Tab
                          label="Apprenants"
                          value="apprenants"
                        />
                        <Tab
                          label="À décaler"
                          value="shift"
                        />
                        <Tab
                          label="Statistiques"
                          value="stats"
                        />
                      </TabList>
                    </Box>
                    <TabPanel value="shift" sx={{ p: 0 }}>
                      <ShiftedLearnersTable />
                    </TabPanel>

                    <TabPanel value="apprenants" sx={{ p: 0 }}>
                      <CustomersFollowUpTable />
                    </TabPanel>

                    <TabPanel value="stats" sx={{ p: 0 }}>
                      <Grid
                        container
                        spacing={3}
                      >
                        <Grid
                          item
                          xs={12}
                        >
                          <ShiftStatistiqueFinished />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sx={{ mt: 1 }}
                        >
                          <ShiftStatistiqueNotFinished />
                        </Grid>
                      </Grid>
                    </TabPanel>
                  </TabContext>
                </Box>
              ) : (
                <Typography
                  sx={{ m: 2 }}
                >
                  Vous n&apos;avez pas le droit de voir cette page
                </Typography>
              )}

            </Grid>
        </Container>
      </Box>
    </>
  );
};

export default Shift;
